import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Button, Grid, Paper, Divider, Typography, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import {
    Logout as LogoutIcon,
    Login,
    Warning as WarningIcon,
    CheckCircle,
    QueryBuilder as QueryBuilderIcon
} from "@mui/icons-material";
import axios from "axios";
import { postData, getData, putData } from "../../services/axiosInstance";
import { API_ENDPOINTS } from "../../services/endpoints";

function MyAttendancePunch({ onStateChange }) {
    const [userData] = useState(() => JSON.parse(localStorage.getItem("userData") || "{}"));
    const [initialLoad, setInitialLoad] = useState(true);
    const [ipAddress, setIpAddress] = useState("");
    const [loading, setLoading] = useState(false);
    const [attendanceData, setAttendanceData] = useState(null);
    const [isCheckedIn, setIsCheckedIn] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [openConfirm, setOpenConfirm] = useState(false);

    const fetchTodayRecord = useCallback(async () => {
        if (!userData?.id) return;
        try {
            const response = await getData(API_ENDPOINTS.getTodayAttendanceRecord(userData.id));
            if (response.status === "SUCCESS" && response.result) {
                const record = response.result;
                setAttendanceData(record);

                const checkedIn = !!(record.check_in && !record.check_out);
                const completed = !!(record.check_in && record.check_out);

                setIsCheckedIn(checkedIn);
                setIsCompleted(completed);

                // 2. Safely call the prop callback
                if (onStateChange) onStateChange(checkedIn);
            }
        } catch (error) {
            return null;
        } finally {
            setInitialLoad(false);
        }
    }, [userData?.id, onStateChange]);

    useEffect(() => {
        const fetchIp = async () => {
            try {
                const res = await axios.get('https://api.ipify.org?format=json');
                setIpAddress(res.data.ip);
            } catch (err) {
                return null;
            }
        };
        fetchIp();
        fetchTodayRecord();
    }, [fetchTodayRecord]);

    const handleButtonClick = () => {
        if (isCheckedIn) {
            setOpenConfirm(true);
        } else {
            processAttendance("IN");
        }
    };

    const processAttendance = async (type) => {
        if (!userData?.id) return;
        setOpenConfirm(false);
        setLoading(true);

        try {
            if (type === "OUT") {
                const payload = {
                    user_id: userData.id.toString(),
                    check_out: new Date().toISOString(),
                    ip_address: ipAddress || "0.0.0.0"
                };
                const response = await putData(API_ENDPOINTS.updateTodayAttendance, payload);
                if (response.status === "SUCCESS") {
                    await fetchTodayRecord();
                }
            } else {
                const payload = {
                    user_id: userData.id.toString(),
                    ip_address: ipAddress || "0.0.0.0"
                };
                const response = await postData(API_ENDPOINTS.markAttendance, payload);
                if (response.status === "SUCCESS") {
                    await fetchTodayRecord();
                }
            }
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (dateString) => {
        if (!dateString) return "--:--";
        return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    };

    if (initialLoad) {
        return (
            <Box sx={{ py: 6, textAlign: 'center' }}>
                <CircularProgress size={40} />
                <Typography sx={{ mt: 2 }} color="text.secondary">Syncing status...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ textAlign: 'center' }}>
            {isCompleted ? (
                <Box sx={{ my: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: '#2e7d32' }}>
                    <CheckCircle />
                    <Typography variant="body1" fontWeight={700}>
                        Today's attendance has been marked.
                    </Typography>
                </Box>
            ) : (
                <Button
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : (isCheckedIn ? <LogoutIcon /> : <Login />)}
                    onClick={handleButtonClick}
                    sx={{
                        bgcolor: isCheckedIn ? '#ef4444' : '#4caf50',
                        '&:hover': { bgcolor: isCheckedIn ? '#dc2626' : '#43a047' },
                        px: 4, py: 1.2, borderRadius: '12px',
                        fontSize: '1rem', fontWeight: 700, mb: 4,
                        boxShadow: '0 4px 14px 0 rgba(0,0,0,0.1)',
                        minWidth: '160px',
                        my: 4
                    }}
                >
                    {loading ? 'Processing...' : (isCheckedIn ? 'CHECK-OUT' : 'CHECK-IN')}
                </Button>
            )}

            <Dialog
                open={openConfirm}
                onClose={() => setOpenConfirm(false)}
                PaperProps={{ sx: { borderRadius: 3, p: 1, maxWidth: '400px' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="warning" />
                    <Typography variant="h6" fontWeight={700}>Confirm Check-Out</Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" color="text.secondary">
                        Are you sure you want to end your session for today?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setOpenConfirm(false)} sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => processAttendance("OUT")}
                        variant="contained"
                        color="error"
                        sx={{ borderRadius: 2, fontWeight: 700, px: 3 }}
                    >
                        Yes, Check-Out
                    </Button>
                </DialogActions>
            </Dialog>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 4 }}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: '16px', border: '1px solid #c8e6c9' }}>
                        <Login sx={{ color: '#2e7d32', mb: 1 }} />
                        <Divider sx={{ width: '40%', mx: 'auto', my: 1, borderStyle: 'dotted' }} />
                        <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 700, display: 'block' }}>Check In</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatTime(attendanceData?.check_in)}</Typography>
                    </Paper>
                </Grid>

                <Grid size={{ xs: 4 }}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: '#ffebee', borderRadius: '16px', border: '1px solid #ffcdd2' }}>
                        <LogoutIcon sx={{ color: '#d32f2f', mb: 1 }} />
                        <Divider sx={{ width: '40%', mx: 'auto', my: 1, borderStyle: 'dotted' }} />
                        <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 700, display: 'block' }}>Check Out</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatTime(attendanceData?.check_out)}</Typography>
                    </Paper>
                </Grid>

                <Grid size={{ xs: 4 }}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: '#f3e5f5', borderRadius: '16px', border: '1px solid #e1bee7' }}>
                        <QueryBuilderIcon sx={{ color: '#7b1fa2', mb: 1 }} />
                        <Divider sx={{ width: '40%', mx: 'auto', my: 1, borderStyle: 'dotted' }} />
                        <Typography variant="caption" sx={{ color: '#7b1fa2', fontWeight: 700, display: 'block' }}>Total Hours</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{attendanceData?.total_hours || "00:00:00"}</Typography>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

export default MyAttendancePunch;